import React from 'react';
import { 
  Box, 
  Typography, 
  Container, 
  Paper, 
  Avatar, 
  useTheme,
  Stack
} from '@mui/material';
import LinkedInIcon from '@mui/icons-material/LinkedIn';
import GitHubIcon from '@mui/icons-material/GitHub';
import TwitterIcon from '@mui/icons-material/Twitter';

interface TeamMemberProps {
  name: string;
  role: string;
  bio: string;
  avatar: string;
  socialLinks?: {
    linkedin?: string;
    github?: string;
    twitter?: string;
  };
}

const TeamMember: React.FC<TeamMemberProps> = ({ name, role, bio, avatar, socialLinks }) => {
  const theme = useTheme();
  
  return (
    <Paper
      elevation={2}
      sx={{
        p: 3,
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        borderRadius: 2,
        transition: 'transform 0.3s, box-shadow 0.3s',
        '&:hover': {
          transform: 'translateY(-5px)',
          boxShadow: '0 10px 20px rgba(0,0,0,0.1)',
        },
        background: theme.palette.background.paper,
      }}
    >
      <Avatar 
        src={avatar} 
        alt={name}
        sx={{ 
          width: 120, 
          height: 120, 
          mb: 2,
          border: `3px solid ${theme.palette.primary.main}`
        }}
      />
      <Typography variant="h5" component="h3" gutterBottom sx={{ fontWeight: 600 }}>
        {name}
      </Typography>
      <Typography variant="subtitle1" color="primary" gutterBottom>
        {role}
      </Typography>
      <Typography variant="body2" color="textSecondary" align="center" sx={{ mb: 2 }}>
        {bio}
      </Typography>
      
      {socialLinks && (
        <Box sx={{ display: 'flex', gap: 1, mt: 'auto' }}>
          {socialLinks.linkedin && (
            <a href={socialLinks.linkedin} target="_blank" rel="noopener noreferrer" style={{ color: theme.palette.text.secondary }}>
              <LinkedInIcon />
            </a>
          )}
          {socialLinks.github && (
            <a href={socialLinks.github} target="_blank" rel="noopener noreferrer" style={{ color: theme.palette.text.secondary }}>
              <GitHubIcon />
            </a>
          )}
          {socialLinks.twitter && (
            <a href={socialLinks.twitter} target="_blank" rel="noopener noreferrer" style={{ color: theme.palette.text.secondary }}>
              <TwitterIcon />
            </a>
          )}
        </Box>
      )}
    </Paper>
  );
};

const Team: React.FC = () => {
  const theme = useTheme();
  
  const teamMembers: TeamMemberProps[] = [
    {
      name: "Alex Johnson",
      role: "Founder & CEO",
      bio: "Alex has over 15 years of experience in software development and has led multiple successful tech startups.",
      avatar: "/team/alex.jpg",
      // socialLinks: {
      //   linkedin: "https://linkedin.com",
      //   github: "https://github.com",
      //   twitter: "https://twitter.com"
      // }
    },
    {
      name: "Sarah Chen",
      role: "CTO",
      bio: "Sarah is an expert in backend systems and cloud infrastructure with a passion for creating efficient, scalable solutions.",
      avatar: "/team/sarah.jpg",
      // socialLinks: {
      //   linkedin: "https://linkedin.com",
      //   github: "https://github.com"
      // }
    },
    {
      name: "Michael Rodriguez",
      role: "Lead Developer",
      bio: "Michael specializes in frontend development and creating intuitive user interfaces that enhance user experience.",
      avatar: "/team/michael.jpg",
      // socialLinks: {
      //   github: "https://github.com",
      //   twitter: "https://twitter.com"
      // }
    },
    {
      name: "Emily Taylor",
      role: "Product Manager",
      bio: "Emily bridges the gap between technical and business aspects, ensuring our products meet customer needs.",
      avatar: "/team/emily.jpg",
      // socialLinks: {
      //   linkedin: "https://linkedin.com"
      // }
    }
  ];
  
  return (
    <Box 
      id="team" 
      sx={{ 
        py: 8,
        background: theme.palette.mode === 'dark' 
          ? theme.palette.background.default
          : '#f9f9f9'
      }}
    >
      <Container maxWidth="lg">
        <Box sx={{ mb: 6, textAlign: 'center' }}>
          <Typography 
            variant="h3" 
            component="h2" 
            gutterBottom
            sx={{ fontWeight: 'bold' }}
          >
            Our Team
          </Typography>
          <Typography 
            variant="h6" 
            color="textSecondary" 
            sx={{ maxWidth: 700, mx: 'auto' }}
          >
            Meet the talented people behind Web Starter who are dedicated to helping you package and distribute your web applications.
          </Typography>
        </Box>
        
        <Stack 
          direction={{ xs: 'column', md: 'row' }} 
          spacing={3}
          sx={{
            '& > *': {
              width: { xs: '100%', md: 'calc(25% - 18px)' }
            }
          }}
        >
          {teamMembers.map((member, index) => (
            <TeamMember 
              key={index}
              name={member.name}
              role={member.role}
              bio={member.bio}
              avatar={member.avatar}
              socialLinks={member.socialLinks}
            />
          ))}
        </Stack>
      </Container>
    </Box>
  );
};

export default Team;
